import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { validateProps } from '../../../../../utils/propValidator';
import { FormFieldWrapper } from '../../layout';

const propRules = {
  id: { type: 'string', required: true },
  checked: { type: 'boolean', required: true },
  onChange: { type: 'function', required: true },

  label: { type: 'string' },
  disabled: { type: 'boolean' },

  error: { type: 'boolean' },
  errorMessage: { type: 'string' },
  helperText: { type: 'string' },
  showErrorInline: { type: 'boolean' },
  reserveErrorSpace: { type: 'boolean' },

  sx: { type: 'object' },
  wrapperSx: { type: 'object' },
};

/**
 * BaseCheckbox
 *
 * Pure controlled checkbox component with inline label and
 * optional inline error and helper text support.
 *
 * This component has no form awareness and must be controlled
 * by the parent via `checked` and `onChange`.
 *
 * @param {Object} props
 * @param {string} props.id - DOM id for the underlying checkbox input.
 * @param {boolean} props.checked - Controlled checked state.
 * @param {(checked: boolean) => void} props.onChange - Called when the checked state changes.
 * @param {string} [props.label] - Inline label rendered next to the checkbox.
 * @param {boolean} [props.disabled=false] - Disables the checkbox when true.
 * @param {boolean} [props.error=false] - Whether the checkbox is in an error state.
 * @param {string} [props.errorMessage] - Error message rendered inline when `error` is true.
 * @param {string} [props.helperText] - Helper text rendered alongside the error (e.g. guidance text).
 * @param {boolean} [props.showErrorInline=true] - Controls whether the error is rendered inline by this component.
 * @param {boolean} [props.reserveErrorSpace=false] - Reserves vertical space for error/helper text to prevent layout shift.
 * @param {Object} [props.sx] - MUI sx styles applied to the Checkbox component.
 * @param {Object} [props.wrapperSx] - MUI sx styles applied to the FormFieldWrapper.
 *
 * @returns {React.ReactElement}
 */
export default function BaseCheckbox(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'BaseCheckbox');

  const {
    id,
    checked,
    onChange,
    label,
    disabled = false,

    error = false,
    errorMessage,
    helperText,
    showErrorInline = true,
    reserveErrorSpace = false,

    sx = {},
    wrapperSx = {},
  } = props;

  return (
    <FormFieldWrapper
      error={showErrorInline ? error : false}
      errorMessage={showErrorInline ? errorMessage : undefined}
      helperText={helperText}
      reserveErrorSpace={reserveErrorSpace}
      sx={wrapperSx}
    >
      <Box component='label' htmlFor={id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          disableRipple
          size='small'
          sx={{
            padding: 0,
            color: 'grey.500',
            '&.Mui-checked': { color: 'grey.700' },
            '&.Mui-disabled': { color: 'grey.700', opacity: 0.3 },
            ...sx,
          }}
        />
        {label ? (
          <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        ) : null}
      </Box>
    </FormFieldWrapper>
  );
}
